import { createApi } from '@reduxjs/toolkit/query/react'
import { LatLngBoundsExpression, LatLngLiteral, LatLngTuple } from 'leaflet'
import { WorkOrderTypeList } from 'services/Engagement/types'
import { DropdownOptionTemplate } from 'types/common'
import axiosBaseQuery from './axiosBaseQuery'
import {
  AutoNamingConfigResponse,
  DetailFieldType,
  UnreadNotificationsCountResponse,
} from './types'

type GetLocationRequest = {
  clientId: number | undefined
}

type GetLocationResponse = {
  latitude: number
  longitude: number
}

type GetModuleLocationRequest = {
  clientId: number | undefined
  module: 'asset' | 'ops' | 'fleet' | 'engagement'
}

type GetModuleLocationResponse = LatLngBoundsExpression

type DeviceTypesResponse = DropdownOptionTemplate

type GetLanguagesResponse = {
  languages: Array<[string, string]>
}

type GetUserOperationsRequest = {
  clientId?: number
}
type GetUserOperationsResponse = Array<
  Record<string, null | number | boolean | string | Array<number> | Array<string>> & {
    id: number
    name: string
  }
>

export type ClientLocationResponse = {
  latitude: number
  longitude: number
}

export type SearchServicePointResponse = DropdownOptionTemplate
export type SearchServicePointRequest = {
  clientId: number | undefined
  search: string
  operations?: number[]
}

type QRConfigRequest = { clientId: number } & (
  | { appLabel: 'MRF'; model: 'inbound' | 'parcel' }
  | { appLabel: 'evrekaTest'; model: 'bin' }
)

type QRFieldsRequest = QRConfigRequest & { id_list: Array<string | number> }

type QRConfigResponse = {
  enabled: boolean
  dynamic_fields: Array<string>
  static_fields: Array<string>
}

export type QRFieldType = DetailFieldType<string | number | null> & {
  qr_field: boolean
  id?: number | string
  options?: Array<{ value: number | string; label: string }>
}

export type QRFieldsResponse = Array<
  Record<string, QRFieldType> & {
    dynamic: Record<string, QRFieldType>
  }
>

export type ClientFeaturesResponse = {
  client_id: string
  features: {
    activities: boolean
    qr: boolean
    sync_static_tour: boolean
    mfa: boolean
    waste_transfer_note: boolean
    qhse_auto_mail: boolean
    'settings:waste_administration': boolean
    rfid_chip_management: boolean
  }
}

export const commonApi = createApi({
  reducerPath: 'commonApi',
  tagTypes: ['UnreadNotificationsCount'],
  baseQuery: axiosBaseQuery({
    baseUrl: window.envVariables.REACT_APP_BASE_URL,
  }),
  endpoints: (builder) => ({
    getLocation: builder.query<GetLocationResponse, GetLocationRequest>({
      query: ({ clientId }) => ({ url: `/api/v2/clients/${clientId}/get_location`, method: 'GET' }),
    }),
    getModuleLocation: builder.query<GetModuleLocationResponse, GetModuleLocationRequest>({
      query: ({ clientId, module }) => ({
        url: `/api/v2/clients/${clientId}/get_module_location/?module=${module}`,
        method: 'GET',
      }),
      transformResponse: ({
        _northEast,
        _southWest,
      }: {
        _northEast: { lat: number; lng: number }
        _southWest: { lat: number; lng: number }
      }) => {
        return [
          [_northEast.lat, _northEast.lng],
          [_southWest.lat, _southWest.lng],
        ]
      },
    }),
    getDeviceTypes: builder.query<DeviceTypesResponse, void>({
      query: () => ({ url: `/api/v2/device_types/`, method: 'GET' }),
    }),
    //TODO: speak with be to move api from /ops to common
    getClientLocation: builder.query<ClientLocationResponse, number | undefined>({
      query: (clientId) => ({
        url: `/api/clients/${clientId}/ops_management/get_client_location/`,
        method: 'GET',
      }),
    }),
    getClientDetails: builder.query<LatLngLiteral, string>({
      query: (clientId) => ({
        url: `/engagement/clients/${clientId}/detail/`,
        method: 'GET',
      }),
      transformResponse: ({ latitude, longitude }: ClientLocationResponse) => {
        return {
          lat: latitude,
          lng: longitude,
        }
      },
    }),
    getLanguages: builder.query<GetLanguagesResponse, void>({
      query: () => ({
        url: '/api/languages/',
        method: 'GET',
      }),
    }),
    getUserOperations: builder.query<GetUserOperationsResponse, GetUserOperationsRequest>({
      query: ({ clientId }) => ({
        url: `/api/clients/${clientId}/operations/`,
        method: 'GET',
      }),
    }),
    //TODO: speak with be to move api from /ops to common ? or entity
    searchServicePoint: builder.mutation<SearchServicePointResponse, SearchServicePointRequest>({
      query: ({ clientId, ...data }) => ({
        url: `/api/clients/${clientId}/ops_management/search_service_point/`,
        method: 'POST',
        data,
      }),
    }),
    setLanguage: builder.mutation<{ language: string }, string>({
      query: (language) => ({
        url: `/set_language_react/`,
        method: 'POST',
        data: {
          language: language === 'zh' ? 'zh-cn' : language,
        },
      }),
    }),
    getMrfLabels: builder.query<WorkOrderTypeList, number>({
      query: (external_client_id) => ({
        url: `/MRF/clients/external/${external_client_id}/label/list/`,
        method: 'GET',
      }),
    }),
    getClientModules: builder.query<{ modules: Array<string> }, number>({
      query: (client_id) => ({
        url: `/int-api/clients/${client_id}/modules/`,
        method: 'GET',
      }),
    }),
    getAutoNamingConfig: builder.query<
      AutoNamingConfigResponse,
      { clientId: number; module_client_id?: number }
    >({
      query: ({ clientId, module_client_id }) => ({
        url: `/int-api/auto_name_configuration/clients/${clientId}/auto_name_configuration/`,
        method: 'GET',
        params: {
          module_client_id,
        },
      }),
    }),
    getQRConfig: builder.query<QRConfigResponse, QRConfigRequest>({
      query: ({ clientId, appLabel, model }) => ({
        url: `/int-api/qr_fields_configuration/clients/${clientId}/qr_field_configuration/${appLabel}/${model}/`,
        method: 'GET',
      }),
    }),
    getQRFields: builder.query<QRFieldsResponse, QRFieldsRequest>({
      query: ({ clientId, appLabel, model, ...data }) => ({
        url: `/int-api/qr_fields_configuration/clients/${clientId}/qr_fields/${appLabel}/${model}/`,
        method: 'POST',
        data,
        errorOverride: true,
      }),
    }),
    getKML: builder.query<LatLngTuple[], string>({
      query: (url) => ({
        url,
        method: 'GET',
        noToken: true,
      }),
      transformResponse: (kml: string): LatLngTuple[] => {
        const parser = new DOMParser()
        const xmlDoc = parser.parseFromString(kml, 'application/xml')
        const coordElements = xmlDoc.getElementsByTagName('coordinates')

        const coordsArray: L.LatLngTuple[] = []
        Array.from(coordElements).forEach((element) => {
          const coords = element?.textContent
            ?.trim()
            .split(/\s+/)
            .map((coord) => {
              const parts = coord.split(',')
              if (parts.length >= 2) {
                return [parseFloat(parts[1]), parseFloat(parts[0])] as L.LatLngTuple
              }
              return undefined
            })
            .filter((coord): coord is L.LatLngTuple => coord !== undefined)

          if (coords) {
            coordsArray.push(...coords)
          }
        })

        return coordsArray
      },
    }),
    getClientFeatures: builder.query<ClientFeaturesResponse, number>({
      query: (clientId) => ({
        url: `/api/clients/${clientId}/ops_management/get_client_features/`,
        method: 'GET',
        errorOverride: true,
      }),
    }),
    getQRImage: builder.query<Blob, { clientId: number; data: string }>({
      query: ({ clientId, data }) => ({
        url: `/int-api/qr_fields_configuration/clients/${clientId}/generate_qr/?data=${data}`,
        method: 'GET',
        errorOverride: true,
        isBlob: true,
      }),
    }),
    getUnreadNotificationsCount: builder.query<
      UnreadNotificationsCountResponse,
      { clientId: number }
    >({
      query: ({ clientId }) => ({
        url: `/int-api/general/unread_notifications/${clientId}/`,
        method: 'GET',
      }),
      providesTags: ['UnreadNotificationsCount'],
    }),
    getEngagementInfo: builder.query<
      {
        id: string
        external_id: number
        name: string
        has_cases_enabled: boolean
        client_detail: {
          is_cases_enabled: boolean
          is_pricing_enabled: boolean
          has_asset_relationship: boolean
          default_tax: number | null
          has_case_client_notification_enabled: boolean
          has_case_rate_enabled: boolean
          is_pass_feature_enabled: boolean
        }
      },
      number // client external id
    >({
      query: (clientId) => ({
        url: `/engagement/clients/${clientId}/info/`,
        method: 'GET',
      }),
    }),
  }),
})

export const {
  useGetLocationQuery,
  useGetModuleLocationQuery,
  useGetDeviceTypesQuery,
  useGetClientLocationQuery,
  useSearchServicePointMutation,
  useSetLanguageMutation,
  useGetClientDetailsQuery,
  useGetLanguagesQuery,
  useGetUserOperationsQuery,
  useGetMrfLabelsQuery,
  useGetClientModulesQuery,
  useGetAutoNamingConfigQuery,
  useGetQRConfigQuery,
  useGetQRFieldsQuery,
  useGetKMLQuery,
  useGetClientFeaturesQuery,
  useGetQRImageQuery,
  useGetUnreadNotificationsCountQuery,
  useGetEngagementInfoQuery,
} = commonApi
